import { BRAND } from "@/config";

/**
 * Registration, waiver and consent agreement parents sign once per version.
 * The exact text is stored with each signature, so changing it requires a new `version`.
 *
 * Same agreement as Triumph Connect (v1.0), with the club-specific parts adjusted: no gym-fee
 * billing, A1 holds tryouts (not zero-cut), refunds go to support@, no IP address recorded.
 */
export const WAIVER = {
  version: "v1.0",
  title: `${BRAND.clubName.toUpperCase()} — REGISTRATION, WAIVER & CONSENT AGREEMENT`,
  intro: `By registering an athlete with ${BRAND.clubName} ("${BRAND.name}") through ${BRAND.appName}, I agree to the following on behalf of my child/ward ("Athlete"):`,
  sections: [
    {
      title: "1. Registration & Payment.",
      body: `Registration is complete only when the applicable fee has been paid. All fees are non-refundable, as ${BRAND.name} manages fees against a committed team budget. Refund requests are not processed through the platform; any request must be directed to ${BRAND.supportEmail}.`,
    },
    {
      title: "2. Team Placement.",
      body: "Coaches retain sole discretion to place, move, or reassign players across rosters and practice groups in the interest of athletic development.",
    },
    {
      title: "3. Assumption of Risk & Release of Liability.",
      body: `I understand that participation in basketball activities — practices, tryouts, camps, games, and events — involves inherent risks of injury, including but not limited to sprains, fractures, concussions, and, in rare cases, catastrophic injury or death. I voluntarily assume all such risks on behalf of the Athlete. To the fullest extent permitted by law, I release, discharge, and hold harmless ${BRAND.clubName}, its owners, coaches, staff, volunteers, facility partners, and Okra USA LLC (the platform operator) from any and all claims, liabilities, or damages arising from the Athlete's participation, except in cases of gross negligence or willful misconduct.`,
    },
    {
      title: "4. Indemnification.",
      body: "I agree to indemnify and hold harmless the parties released above from any claim brought by or on behalf of the Athlete arising from the Athlete's participation, to the fullest extent permitted by law.",
    },
    {
      title: "5. Medical Disclosure & Responsibility.",
      body: `${BRAND.appName} does not collect or store athlete medical information. I agree to inform the Athlete's coach directly of any medical condition, allergy, or limitation relevant to the Athlete's safe participation, before the Athlete's first practice and promptly whenever it changes. I remain solely responsible for the management of any such condition and for its implications during practices, games, and events. I authorize ${BRAND.name} coaches and staff to obtain emergency medical treatment for the Athlete if I cannot be reached in a timely manner, including administering first aid and authorizing transport to a medical facility.`,
    },
    {
      title: "6. Insurance.",
      body: `${BRAND.name} does not provide medical, accident, or health insurance for athletes. I am solely responsible for maintaining adequate insurance coverage for the Athlete.`,
    },
    {
      title: "7. Photo, Video & Media Release.",
      body: `I consent to ${BRAND.name} and Okra USA LLC using photos and videos of the Athlete taken during practices, games, and events for internal team purposes and for marketing and promotional use (website, social media, printed materials), unless I opt out in writing to ${BRAND.supportEmail}.`,
    },
    {
      title: "8. Code of Conduct.",
      body: `I agree to ${BRAND.name}'s zero-tolerance policy on parental coaching from the sidelines and to conduct myself respectfully toward coaches, players, referees, and other families at all times. ${BRAND.name} may suspend participation for violations, without refund.`,
    },
    {
      title: "9. Electronic Signature.",
      body: "By typing my name, checking the box below and submitting this form, I am signing this agreement electronically. My electronic signature — recorded with a timestamp — is legally binding and equivalent to a handwritten signature.",
    },
    {
      title: "10. Acknowledgment & General Terms.",
      body: `I confirm I am the Athlete's parent or legal guardian, am at least 18 years of age, and have authority to sign on the Athlete's behalf. This agreement is governed by the laws of the State of Texas. If any provision is found unenforceable, the remaining provisions remain in full effect. This agreement should be read alongside ${BRAND.appName}'s Terms of Service and Privacy Policy, available at ${BRAND.websiteUrl.replace("https://", "")}/terms and ${BRAND.websiteUrl.replace("https://", "")}/privacy.`,
    },
  ],
} as const;

/** Plain-text version stored with each signature. */
export const WAIVER_TEXT = [
  WAIVER.title,
  "",
  WAIVER.intro,
  "",
  ...WAIVER.sections.map((s) => `${s.title} ${s.body}`),
].join("\n\n");
